/**src\infrastructure\repositories\UserRepository.ts
 * User Repository Implementation
 *
 * Implements IUserRepository using HTTP client to communicate with the API.
 * Maps between API DTOs and Domain entities.
 */

import type { CreateUserData, IUserRepository } from '@domain/repositories';
import type { User } from '@domain/entities';
import { UserEntity } from '@domain/entities';
import type { IHttpClient } from '@application/ports';
import type { UserDTO } from '@application/dto';
import { API_ENDPOINTS } from '@infrastructure/config';

interface UserResponse {
  success?: boolean;
  user: UserDTO;
}

export class UserRepository implements IUserRepository {
  constructor(private readonly httpClient: IHttpClient) { }

  async findById(id: string): Promise<User | null> {
    try {
      const response = await this.httpClient.get<UserDTO>(
        API_ENDPOINTS.users.byId(id)
      );
      return this.mapToEntity(response.data);
    } catch (error) {
      if (this.isNotFoundError(error)) {
        return null;
      }
      throw error;
    }
  }

  async findByEmail(email: string): Promise<User | null> {
    try {
      const response = await this.httpClient.get<UserDTO[]>(
        API_ENDPOINTS.users.byEmail(email)
      );
      const users = response.data;
      return users.length > 0 ? this.mapToEntity(users[0]!) : null;
    } catch (error) {
      if (this.isNotFoundError(error)) {
        return null;
      }
      throw error;
    }
  }

  async create(userData: CreateUserData): Promise<User> {
    const url = API_ENDPOINTS.users.base;

    const body = {
      email: userData.email,
      password: userData.password,
      name: userData.name,
    };

    console.log('CREATE USER URL:', url);
    console.log('CREATE USER BODY:', body);

    const response = await this.httpClient.post<UserDTO | UserResponse>(
      url,
      body
    );

    console.log('CREATE USER RESPONSE:', response.data);

    return this.mapToEntity(this.getUserDTO(response.data));
  }

  async update(id: string, userData: Partial<User>): Promise<User> {
    const response = await this.httpClient.patch<UserDTO>(
      API_ENDPOINTS.users.byId(id),
      {
        name: userData.name,
      }
    );
    return this.mapToEntity(response.data);
  }

  async delete(id: string): Promise<void> {
    await this.httpClient.delete(API_ENDPOINTS.users.byId(id));
  }

  async findAll(): Promise<User[]> {
    const response = await this.httpClient.get<UserDTO[]>(
      API_ENDPOINTS.users.base
    );
    return response.data.map(user => this.mapToEntity(user));
  }

  /**
   * Map UserDTO from API to UserEntity (Domain)
   */
  private mapToEntity(dto: UserDTO): User {
    return new UserEntity(
      String(dto.id),
      dto.email,
      dto.name
    );
  }

  private getUserDTO(data: UserDTO | UserResponse): UserDTO {
    return 'user' in data ? data.user : data;
  }

  /**
   * Check if error is a 404 Not Found error
   */
  private isNotFoundError(error: unknown): boolean {
    return (error as { response?: { status?: number } })?.response?.status === 404;
  }
}
